import { useRouter } from '@repo/ui/hooks/use-router';
import { Button } from '@repo/ui/components/ui/button';
import { ArrowLeft, Download } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { usePrintData } from '../../hooks/use-print-data';
import { usePDFDownload } from '../../print/hooks/use-pdf-download';
import InterventionFormEpaysage from './components/InterventionFormEpaysage';
import { useUserSettings } from '../../../../hooks/api/user-settings';
import { pdf } from '@react-pdf/renderer';

interface InterventionEpaysageProps {
  inventaireId: number;
}

export default function InterventionEpaysage({ inventaireId }: InterventionEpaysageProps) {
  const router = useRouter();
  const { data: userSettingsData } = useUserSettings();
  const [printMode,] = useState(false);

  const { data: printData, isLoading, error } = usePrintData(inventaireId);

  const parseGPS = (
    gps: string | { lat: number; lng: number } | undefined,
  ): { lat: number; lng: number } | undefined => {
    if (!gps) return undefined;
    if (typeof gps === 'object' && 'lat' in gps && 'lng' in gps) {
      return gps;
    }
    if (typeof gps !== 'string') return undefined;
    const parts = gps
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
    if (parts.length < 2) return undefined;
    const lat = Number.parseFloat(parts[0] ?? '');
    const lng = Number.parseFloat(parts[1] ?? '');
    if (Number.isNaN(lat) || Number.isNaN(lng)) return undefined;
    return { lat, lng };
  };

  const handleBack = () => {
    router.back();
  };



  const generatePDF = useMemo(() => {
    return async (): Promise<Blob> => {
      if (!printData) {
        throw new Error('Données non disponibles pour la génération du PDF');
      }

      try {
        const coord = parseGPS((printData as any).gps);
        let staticMapsBase64: { openstreetmap?: string; google?: string; satellite?: string } | null = null;
        if (coord) {
          try {
            const { lat, lng } = coord;

            const LModule = await import('leaflet');
            // @ts-ignore
            await import('leaflet/dist/leaflet.css');
            // @ts-ignore - leaflet-image doesn't have TypeScript types
            const leafletImageModule = await import('leaflet-image');
            const leafletImage = leafletImageModule.default || leafletImageModule;

            // @ts-ignore
            const L = LModule.default || LModule;

            // @ts-ignore
            delete L.Icon.Default.prototype._getIconUrl;
            // @ts-ignore
            L.Icon.Default.mergeOptions({
              iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
              iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
              shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
            });

            const generateMapImage = async (tileUrl: string, subdomains?: string[]): Promise<string> => {
              const mapContainer = document.createElement('div');
              mapContainer.style.width = '400px';
              mapContainer.style.height = '300px';
              mapContainer.style.position = 'absolute';
              mapContainer.style.left = '-9999px';
              mapContainer.style.top = '-9999px';
              document.body.appendChild(mapContainer);

              // @ts-ignore
              const map = L.map(mapContainer, {
                center: [lat, lng],
                zoom: 16,
                zoomControl: false,
                attributionControl: false,
                scrollWheelZoom: false,
                dragging: false,
                doubleClickZoom: false,
                boxZoom: false,
                keyboard: false,
                touchZoom: false,
              });

              // @ts-ignore
              const tileLayer = L.tileLayer(tileUrl, {
                attribution: '',
                maxZoom: 19,
                subdomains: subdomains || ['a', 'b', 'c'],
              });
              tileLayer.addTo(map);

              // @ts-ignore
              L.marker([lat, lng], {
                // @ts-ignore
                icon: L.icon({
                  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
                  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
                  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
                  iconSize: [25, 41],
                  iconAnchor: [12, 41],
                  popupAnchor: [1, -34],
                  shadowSize: [41, 41],
                }),
              }).addTo(map);

              await new Promise<void>((resolve) => {
                map.whenReady(() => {
                  map.once('idle', () => {
                    setTimeout(resolve, 500);
                  });
                });
                setTimeout(resolve, 3000);
              });

              // @ts-ignore
              const imageBase64 = await new Promise<string>((resolve, reject) => {
                leafletImage(map, (err: Error | null, canvas: HTMLCanvasElement | null) => {
                  if (err || !canvas) {
                    reject(err || new Error('Failed to capture map image'));
                    return;
                  }
                  resolve(canvas.toDataURL('image/png', 0.95));
                });
              });

              map.remove();
              document.body.removeChild(mapContainer);

              return imageBase64;
            };

            staticMapsBase64 = {
              openstreetmap: await generateMapImage('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'),
              google: await generateMapImage('https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}', ['mt0', 'mt1', 'mt2', 'mt3']),
              satellite: await generateMapImage('https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}', ['mt0', 'mt1', 'mt2', 'mt3']),
            };
          } catch (mapError) {
            console.warn('Erreur lors de la génération des cartes avec Leaflet:', mapError);
          }
        }

        const { generateEpaysageInterventionPDFDocument } = await import('./generate-intervention-pdf');
        const PDFDocumentComponent = generateEpaysageInterventionPDFDocument({
          printData,
          inventaireId,
          userSettingsData,
          staticMapsBase64: staticMapsBase64 || undefined,
        });

        const blob = await pdf(PDFDocumentComponent).toBlob();

        if (!blob) {
          throw new Error('Impossible de générer le blob PDF');
        }

        return blob;
      } catch (err) {
        console.error('Erreur lors de la génération du PDF:', err);
        throw err instanceof Error ? err : new Error('Erreur inconnue lors de la génération du PDF');
      }
    };
  }, [printData, inventaireId, userSettingsData]);

  const { isGeneratingPDF, handleDownloadPDF } = usePDFDownload(generatePDF, {
    filename: `intervention-epaysage-${inventaireId || 'fiche'}.pdf`,
    onError: (err) => {
      console.error('Erreur génération PDF:', err);
      alert(`Erreur lors de la génération du PDF: ${err.message}`);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-lg">Chargement des données...</p>
        </div>
      </div>
    );
  }

  if (error || !printData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-bold text-red-600">Erreur</h1>
          <p className="text-muted-foreground">
            Impossible de charger les données de l&apos;inventaire
          </p>
          <div className="flex justify-center gap-2">
            <Button onClick={handleBack} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white ${printMode ? 'print-mode' : 'min-h-screen'}`}>
      <div className="fixed top-4 right-4 print:hidden z-50 flex gap-2">
        <Button onClick={handleBack} variant="outline" className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Retour
        </Button>
        <Button onClick={handleDownloadPDF} disabled={isGeneratingPDF || !printData} className="gap-2">
          <Download className="h-4 w-4" />
          {isGeneratingPDF ? 'Génération...' : 'Télécharger PDF'}
        </Button>
      </div>

      <InterventionFormEpaysage printData={printData as any} inventaireId={inventaireId} />
    </div>
  );
}

